import { Link } from 'react-router-dom';
import { useEffect, useMemo, useState, useRef } from 'react';
import {
  ArrowRight,
  Users,
  Sprout,
  ShieldCheck,
  Newspaper,
  Heart,
  Sparkles,
  Quote,
  ChevronDown,
  HandHeart,
} from 'lucide-react';
import { fetchPublishedPosts } from '../lib/db';
import { useSiteSettings } from '../lib/site-settings';
import { useSeo } from '../lib/seo';
import type { Post } from '../lib/types';
import { getHomePageContent } from '../lib/homepage-content';

function AnimatedSection({
  children,
  className = '',
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

function ParticleField() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(20)].map((_, i) => (
        <div
          key={i}
          className="absolute w-1 h-1 rounded-full bg-gold-400/30"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animation: `float ${8 + Math.random() * 4}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 4}s`,
          }}
        />
      ))}
    </div>
  );
}

function StatCounter({ value, label, icon: Icon, color }: { value: string; label: string; icon: React.ElementType; color: string }) {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const numericValue = parseInt(value.replace(/[^0-9]/g, ''));
  const formattedCount = useMemo(() => count.toLocaleString(), [count]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    const duration = 2000;
    const steps = 60;
    const increment = numericValue / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= numericValue) {
        setCount(numericValue);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [isVisible, numericValue]);

  return (
    <div
      ref={ref}
      className="card p-4 text-center hover:shadow-xl transition-all duration-500 hover:-translate-y-2 group cursor-default"
    >
      <div className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 mb-3 transition-transform duration-300 group-hover:scale-110`}>
        <Icon className={`h-5 w-5 ${color}`} />
      </div>
      <p className="font-display text-xl font-bold text-stone-900 md:text-2xl">
        {formattedCount}{value.includes('+') ? '+' : ''}
      </p>
      <p className="mt-1 text-xs text-stone-500 leading-snug">{label}</p>
    </div>
  );
}

export default function Home() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const { settings } = useSiteSettings();
  const content = getHomePageContent(settings);

  const heroTitle = content.hero.title;
  const heroSubtitle = content.hero.subtitle;
  const rawHeroImage = content.hero.imageUrl || 'https://images.unsplash.com/photo-1509099836639-18ba1795216d?auto=format&fit=crop&w=1600&q=80';
  const heroImage = rawHeroImage.trim().replace(/^http:\/\//i, 'https://');
  const newsHeading = content.newsHeading;
  const newsSubheading = content.newsSubheading;

  const stats = [
    {
      icon: Users,
      value: settings.stat_1_value || content.stats[0]?.value || '3,200+',
      label: settings.stat_1_label || content.stats[0]?.label || 'Women & Girls Empowered',
      color: 'text-clay-600',
    },
    {
      icon: ShieldCheck,
      value: settings.stat_2_value || content.stats[1]?.value || '48',
      label: settings.stat_2_label || content.stats[1]?.label || 'Villages FGM-Free',
      color: 'text-brand-600',
    },
    {
      icon: Sprout,
      value: settings.stat_3_value || content.stats[2]?.value || '1,500',
      label: settings.stat_3_label || content.stats[2]?.label || 'Women Trained in Farming',
      color: 'text-green-600',
    },
    {
      icon: HandHeart,
      value: settings.stat_4_value || content.stats[3]?.value || '12,000',
      label: settings.stat_4_label || content.stats[3]?.label || 'Lives Touched',
      color: 'text-gold-600',
    },
  ];

  const seoTitle = 'Kisapuk Community Initiative | Ending FGM and Empowering Women';
  const seoDescription = 'Kisapuk Community Initiative partners with Maasai communities to end female genital mutilation, support women’s farming, and build sustainable change in Kenya.';

  useSeo({
    title: seoTitle,
    description: seoDescription,
    pathname: '/',
    image: heroImage,
  });

  useEffect(() => {
    if (!heroImage) return;

    const preloadLink = document.createElement('link');
    preloadLink.rel = 'preload';
    preloadLink.as = 'image';
    preloadLink.href = heroImage;
    preloadLink.fetchPriority = 'high';
    document.head.appendChild(preloadLink);

    return () => {
      preloadLink.remove();
    };
  }, [heroImage]);

  useEffect(() => {
    fetchPublishedPosts().catch(() => []).then((p) => {
      setPosts(p.slice(0, 3));
      setLoading(false);
    });
  }, []);

  const focusAreaIcons = [ShieldCheck, Sprout, Newspaper, Heart];

  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-center justify-start overflow-hidden bg-brand-950">
        {/* Background image with Ken Burns effect */}
        <div
          className="absolute inset-0 bg-cover bg-center animate-ken-burns"
          style={{
            backgroundImage: `url(${heroImage})`,
          }}
        >
          <div className="absolute inset-0 bg-black/20" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-brand-950/95 via-brand-950/85 to-brand-900/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-950 via-transparent to-brand-950/40" />
        <div className="absolute inset-0 bg-gradient-to-br from-clay-900/20 via-transparent to-gold-900/10" />

        <ParticleField />

        {/* Floating decorative orbs */}
        <div className="absolute top-1/4 right-12 h-72 w-72 rounded-full bg-gold-500/10 blur-3xl animate-float" />
        <div className="absolute bottom-1/4 left-8 h-56 w-56 rounded-full bg-clay-500/10 blur-3xl animate-float-slow" />
        <div className="absolute top-1/2 right-1/4 h-40 w-40 rounded-full bg-brand-400/5 blur-2xl animate-float" style={{ animationDelay: '2s' }} />

        <div className="relative container-prose mx-0 pl-4 sm:pl-6 lg:pl-12 py-20 z-10">
          <div className="max-w-2xl text-left ml-0 mr-auto lg:mr-auto">
            <div className="inline-flex items-center gap-2 rounded-full bg-gold-500/20 px-3 py-1.2 text-xs sm:text-sm font-medium text-gold-300 backdrop-blur-sm mb-5 animate-fade-up">
              <Sparkles className="h-4 w-4 animate-bounce-subtle" />
              {content.hero.badge}
            </div>
            <h1 className="font-display text-3xl font-bold leading-tight text-white sm:text-4xl lg:text-5xl animate-fade-up-delay max-w-xl whitespace-pre-line">
              {heroTitle}
            </h1>
            <p className="mt-5 text-base text-brand-100 leading-relaxed max-w-xl animate-fade-up-delay-2">
              {heroSubtitle}
            </p>
            <div className="mt-8 flex flex-wrap gap-4 animate-fade-up-delay-3">
              <Link to={content.hero.primaryCtaLink} className="btn-gold group">
                {content.hero.primaryCtaText}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to={content.hero.secondaryCtaLink}
                className="btn border-2 border-white/30 text-white hover:bg-white/10 backdrop-blur-sm transition-all duration-300 hover:border-white/50"
              >
                {content.hero.secondaryCtaText}
              </Link>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.35em] text-white/75">
              {content.hero.eyebrowItems.map((item, index) => (
                <span key={item}>
                  {item}
                  {index < content.hero.eyebrowItems.length - 1 ? <span className="hidden sm:inline"> • </span> : null}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2">
          <span className="text-xs text-white/70 uppercase tracking-widest">Scroll</span>
          <ChevronDown className="h-6 w-6 text-gold-400 animate-bounce" />
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-24" />
      </section>

      {/* Stats */}
      <section className="relative container-prose pt-20 lg:pt-24">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
          {stats.map((stat, i) => (
            <AnimatedSection key={stat.label} delay={i * 100}>
              <StatCounter {...stat} />
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* Focus Areas with images */}
      <section className="py-20 lg:py-28">
        <div className="container-prose">
          <AnimatedSection className="mx-auto max-w-2xl text-center mb-14">
            <span className="badge-gold mb-4 inline-block">What We Do</span>
            <h2 className="font-display text-3xl font-bold text-stone-900 sm:text-4xl">
              {content.focusAreasHeading}
            </h2>
            <p className="mt-4 text-lg text-stone-600">
              {content.focusAreasSubtitle}
            </p>
          </AnimatedSection>

          <div className="grid gap-8 md:grid-cols-2">
            {content.focusAreas.map((area, i) => {
              const Icon = focusAreaIcons[i % focusAreaIcons.length];
              return (
                <AnimatedSection key={area.title} delay={i * 150}>
                  <div className="group card overflow-hidden hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                    <div className="relative h-56 overflow-hidden">
                      <img
                        src={area.imageUrl}
                        alt={area.title}
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-brand-950/90 via-brand-950/40 to-transparent" />
                      <div className="absolute bottom-4 left-4 flex items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gold-500 text-stone-900 shadow-lg transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                          <Icon className="h-6 w-6" />
                        </div>
                        <h3 className="font-display text-xl font-bold text-white">{area.title}</h3>
                      </div>
                    </div>
                    <div className="p-6">
                      <p className="text-sm text-stone-600 leading-relaxed">{area.description}</p>
                    </div>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mission Banner */}
      <section className="relative py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/162539/architecture-building-amsterdam-blue-sky-162539.jpeg"
            alt=""
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-brand-950/90" />
          <div className="absolute inset-0 bg-gradient-to-r from-clay-900/30 via-transparent to-gold-900/20" />
        </div>
        <div className="relative container-prose">
          <AnimatedSection className="mx-auto max-w-3xl text-center">
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gold-500/20 text-gold-400 mb-6 animate-pulse">
              <Heart className="h-8 w-8" />
            </div>
            <h2 className="font-display text-3xl font-bold text-white sm:text-4xl">
              Our Mission
            </h2>
            <p className="mt-6 text-lg text-brand-100 leading-relaxed">
              To create a future where every Maasai girl is safe from harmful traditions, every woman
              has the skills to feed her family and earn a living, and every community has a voice in
              shaping its own story. We believe change must come from within — led by elders, mothers,
              youth, and leaders who know their community best.
            </p>
            <Link to="/about" className="btn-gold mt-8 group">
              Learn More About Us
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 lg:py-28 bg-stone-50">
        <div className="container-prose">
          <AnimatedSection className="mx-auto max-w-2xl text-center mb-14">
            <span className="badge-clay mb-4 inline-block">Voices of Change</span>
            <h2 className="font-display text-3xl font-bold text-stone-900 sm:text-4xl">
              {content.testimonialsHeading}
            </h2>
            <p className="mt-4 text-lg text-stone-600">
              {content.testimonialsSubtitle}
            </p>
          </AnimatedSection>

          <div className="grid gap-6 md:grid-cols-3">
            {content.testimonials.map((t, i) => (
              <AnimatedSection key={t.name} delay={i * 150}>
                <div className="card p-8 flex flex-col h-full hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group">
                  <Quote className="h-8 w-8 text-gold-400 mb-4 transition-transform duration-300 group-hover:scale-110" />
                  <p className="text-stone-700 leading-relaxed flex-1 italic">"{t.quote}"</p>
                  <div className="mt-6 pt-4 border-t border-stone-100">
                    <p className="font-display font-semibold text-stone-900">{t.name}</p>
                    <p className="text-sm text-stone-500">{t.role}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Latest News */}
      <section className="py-20 lg:py-28">
        <div className="container-prose">
          <AnimatedSection className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-12">
            <div>
              <span className="badge-brand mb-3 inline-block">Latest Updates</span>
              <h2 className="font-display text-3xl font-bold text-stone-900 sm:text-4xl">
                {newsHeading}
              </h2>
              <p className="mt-3 max-w-2xl text-sm text-stone-600">{newsSubheading}</p>
            </div>
            <Link to="/programs" className="btn-outline group">
              View All
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </AnimatedSection>

          {loading ? (
            <div className="grid gap-6 md:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="card overflow-hidden animate-pulse">
                  <div className="h-48 bg-stone-200" />
                  <div className="p-6 space-y-3">
                    <div className="h-4 bg-stone-200 rounded w-20" />
                    <div className="h-6 bg-stone-200 rounded w-full" />
                    <div className="h-4 bg-stone-200 rounded w-3/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <p className="text-center text-stone-500 py-12">No posts published yet.</p>
          ) : (
            <div className="grid gap-6 md:grid-cols-3">
              {posts.map((post, i) => (
                <AnimatedSection key={post.id} delay={i * 100}>
                  <Link
                    to={`/programs/${post.slug}`}
                    className="card overflow-hidden group hover:shadow-xl transition-all duration-500 hover:-translate-y-2 block"
                  >
                    <div className="relative h-48 overflow-hidden bg-stone-200">
                      {post.cover_image_url && (
                        <img
                          src={post.cover_image_url}
                          alt={post.title}
                          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                      )}
                      <span className="absolute top-3 left-3 badge-gold capitalize">{post.category}</span>
                    </div>
                    <div className="p-6">
                      <p className="text-xs text-stone-500 mb-2">
                        {new Date(post.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </p>
                      <h3 className="font-display text-lg font-semibold text-stone-900 group-hover:text-brand-700 transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="mt-2 text-sm text-stone-600 line-clamp-2">{post.excerpt}</p>
                    </div>
                  </Link>
                </AnimatedSection>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 overflow-hidden bg-gradient-to-br from-clay-600 via-clay-700 to-brand-800">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 h-56 w-56 rounded-full bg-gold-400 blur-3xl animate-float" />
          <div className="absolute bottom-0 right-0 h-72 w-72 rounded-full bg-white blur-3xl animate-float-slow" />
        </div>
        <div className="relative container-prose text-center">
          <AnimatedSection>
            <h2 className="font-display text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
              {content.ctaHeading}
            </h2>
            <p className="mt-6 text-lg text-clay-100 max-w-2xl mx-auto leading-relaxed">
              {content.ctaSubtitle}
            </p>
            <Link to={content.ctaButtonLink} className="btn bg-white text-clay-800 hover:bg-gold-100 mt-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              {content.ctaButtonText}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
